import { useMemo, useState } from 'react';
import { BookmarkPlus, MessageSquareText, Users } from 'lucide-react';
import type { RenderedSavedReply } from '@peles/shared';
import { Popover } from '../common/Popover';
import { useRenderSavedReply, useSavedReplies } from '../../hooks/useSavedReplies';
import type { MergeContext } from '../../api/savedReplies';

export interface SavedReplyPickerProps {
  /** The record(s) the message is about; their details fill the reply's tags. */
  record?: MergeContext;
  /** The reply, filled in, to put into the draft. */
  /** The reply, filled in — its `filledFrom` says whose details it used. */
  onInsert: (reply: RenderedSavedReply) => void;
  /** Offered as "Save this message as a reply" when the draft has something in it. */
  onSaveDraft?: () => void;
  disabled?: boolean;
  onError?: (message: string) => void;
}

/**
 * "Saved replies" — a button beside a message body that lists the writer's
 * own replies and the office's shared ones, fills the chosen one against the
 * record, and hands it back. It never edits the draft itself: where the text
 * goes, and in what format, is the composer's business.
 */
export function SavedReplyPicker({
  record,
  onInsert,
  onSaveDraft,
  disabled,
  onError,
}: SavedReplyPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const replies = useSavedReplies(open);
  const render = useRenderSavedReply();

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = replies.data ?? [];
    if (!term) return all;
    return all.filter(
      (r) => r.name.toLowerCase().includes(term) || r.body.toLowerCase().includes(term),
    );
  }, [replies.data, search]);

  function choose(id: string, close: () => void) {
    render.mutate(
      { id, record },
      {
        onSuccess: (filled) => {
          onInsert(filled);
          setSearch('');
          close();
        },
        onError: (err) => onError?.(err.message || 'That reply could not be filled in.'),
      },
    );
  }

  return (
    <Popover
      ariaLabel="Saved replies"
      align="end"
      panelClassName="w-80"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          onClick={() => {
            setOpen(true);
            props.onClick();
          }}
          disabled={disabled}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50"
          data-testid="saved-replies-button"
        >
          <MessageSquareText size={13} />
          Saved replies
        </button>
      )}
    >
      {(close) => (
        <div className="p-2 space-y-2" data-testid="saved-replies-panel">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find a reply"
            aria-label="Find a saved reply"
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <div className="max-h-64 overflow-y-auto">
            {replies.isLoading && <p className="px-2 py-3 text-xs text-gray-500">Loading…</p>}
            {replies.isError && (
              <p className="px-2 py-3 text-xs text-red-600">Saved replies could not be loaded.</p>
            )}
            {replies.data && shown.length === 0 && (
              <p className="px-2 py-3 text-xs text-gray-500" data-testid="saved-replies-empty">
                {replies.data.length === 0
                  ? 'No saved replies yet. Write a message and save it as one.'
                  : 'Nothing matches.'}
              </p>
            )}
            <ul>
              {shown.map((reply) => (
                <li key={reply.id}>
                  <button
                    type="button"
                    onClick={() => choose(reply.id, close)}
                    disabled={render.isPending}
                    className="w-full text-left px-2 py-1.5 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    data-testid={`saved-reply-${reply.id}`}
                  >
                    <span className="flex items-center gap-1.5 text-sm text-gray-900">
                      {reply.name}
                      {reply.isShared && (
                        <Users size={12} className="text-gray-400" aria-label="Shared" />
                      )}
                    </span>
                    <span className="block text-xs text-gray-500 truncate">{reply.body}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {onSaveDraft && (
            <button
              type="button"
              onClick={() => {
                close();
                onSaveDraft();
              }}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-primary-700 border-t border-gray-100 hover:bg-primary-50"
              data-testid="save-draft-as-reply"
            >
              <BookmarkPlus size={13} />
              Save this message as a reply
            </button>
          )}
        </div>
      )}
    </Popover>
  );
}
